import {AfterViewInit, Component, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {BehaviorSubject, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import {ECMember, ECMemberParticipationType, ECMemberPOD, ECMemberState, EnergyCommunity, EntityType} from 'smag-commons/cfg/hierarchy';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {catchError, debounceTime, filter, first, flatMap, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {can} from '../../shared/permissions';
import {SmagApiService} from '../../api/smag-api.service';
import {ECMembersUploadTemplateType, MISSING_CHECKS, Permission, User} from 'smag-commons/dto/api';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {downloadBlob, errorCode, isDefined} from '../../commons/commons';
import {ALIGN_TITLE, DialogService} from '../../shared/dialog/dialog.service';
import {FormCommunity} from './new-member/new-ec-member.component';
import {mkMemberName} from '../../shared/energy-community';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {ErrorCode, mkBaseError} from '../../api/errors';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../shared/i18n-mat-paginator-intl';
import {MemberAccount} from './account';
import {SpinnerData} from '../../shared/spinner/spinner.component';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {ECMemberStatePipe, GenericPipe} from '../../shared/shared.pipe';
import {UploadData} from './members-massive-import/members-massive-import.component';
import {EnergyCommunityPodSpatialCoordinateRequest, SubjectUploadError, UploadedSubjectInfo} from 'smag-commons/dto/api/massive-upload';
import {GoogleApiService} from '../../api/google-api.service';
import * as _ from 'lodash';
import {STATISTICS_ERROR_POD_KEY, StatisticsError, StatisticsErrorType} from 'smag-commons/dto/api/statistics-errors';
import {DEFAULT_DIALOG_MAX_WIDTH} from '../../shared/ec-economics';
import {addressToString} from '../../shared/ec-members';

interface Community {
    entity?: HierarchyEntity; // can be `null` (e.g. if no energy community is selected)
    canView?: boolean;
    canUpdate?: boolean;
    community?: EnergyCommunity;
}

interface Resource {
    entity: HierarchyEntity;
    canView: boolean;
}

interface Pod {
    pod: ECMemberPOD;
    resource?: Resource;
}

export interface Member {
    name: string;
    member: ECMember;
    pods: Pod[];
    resources: Resource[]; // "flattened" resources for all PODs
    account?: MemberAccount;
}

interface Members {
    members: Member[];
    total: number;
}

export interface PodResource {
    pod: string;
    entity: HierarchyEntity;
}

interface PodMember {
    pod: string;
    name: string;
}

enum UpdateReason {
    USER_ACCOUNT_STATE,
    USER_AUTH, // password change, 2FA
    STATE, // state changes, such as "invitation"
    FIELDS // member's "fields", including POD creation/elimination
}

enum SortCriterion {
    CREATED_AT = 'created_at',
    LAST_NAME = 'last_name',
    COMPANY_NAME = 'company_name',
    REGISTERED_SINCE = 'registered_since'
}

enum SortDirection {
    ASC = 'asc',
    DESC = 'desc'
}

const MEMBER_STATES = [
    ECMemberState.NOT_INVITED,
    ECMemberState.PENDING,
    ECMemberState.REGISTERED,
    ECMemberState.CANCELED
];

const PARTICIPATION_TYPES = [
    'All states',
    ECMemberParticipationType.MEMBER,
    ECMemberParticipationType.THIRD_PARTY_PRODUCER
];

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.CREATED_AT, SortDirection.DESC],
    [SortCriterion.LAST_NAME, SortDirection.ASC],
    [SortCriterion.COMPANY_NAME, SortDirection.ASC],
    [SortCriterion.REGISTERED_SINCE, SortDirection.DESC]
];

const PAGE_SIZE = 15;
const PAGE_SIZES = [15, 30, 50];
const DIALOG_WIDTH = 700;
const SEARCH_DEBOUNCE_MILLIS = 250;

@Component({
    selector: 'app-ec-members',
    templateUrl: './ec-members.component.html',
    styleUrls: ['./ec-members.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Members per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECMembersComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadMembers: Subject<void>;
    private searchForm: FormGroup;
    private sortCriteria: [SortCriterion, SortDirection][];
    private msp: ECMemberStatePipe;
    private genericPipe: GenericPipe;
    @ViewChildren('paginator', {emitDistinctChangesOnly: true}) private paginator: QueryList<MatPaginator>;
    @ViewChild('newMember') private newMemberDialog: TemplateRef<any>;
    @ViewChild('deleteMember') private deleteMemberDialog: TemplateRef<any>;
    @ViewChild('membersNotCreated') private memberNotCreatedDialog: TemplateRef<any>;
    @ViewChild('disableMember') private disableMemberDialog: TemplateRef<any>;
    @ViewChild('deleteMemberUser') private deleteMemberUserDialog: TemplateRef<any>;
    @ViewChild('forcePwdChange') private forcePwdChangeDialog: TemplateRef<any>;
    @ViewChild('force2FAReset') private force2FAResetDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    @ViewChild('uploadMembers') private uploadMembersDialog: TemplateRef<any>;
    @ViewChild('uploadMembersError') private uploadMembersError: TemplateRef<any>;

    displayedColumns: string[];
    community: BehaviorSubject<Community>;
    members: BehaviorSubject<Members>;
    expandedMember: BehaviorSubject<Member>;
    templateIsDownloading = false;

    massiveImportErrorsList: string[] = [];
    massiveImportErrorsPageSize = 10;
    massiveImportErrorsCurrentPage = 1;
    massiveImportErrorsTotalPages: number;
    massiveImportErrorsListPage: string[] = [];

    failedSubjectsList: string[] = [];
    failedSubjectsPageSize = 10;
    failedSubjectsCurrentPage = 1;
    failedSubjectsTotalPages: number;
    failedSubjectsListPage: string[] = [];

    successfulSubjectsList: string[] = [];
    successfulSubjectsPageSize = 10;
    successfulSubjectsCurrentPage = 1;
    successfulSubjectsTotalPages: number;
    successfulSubjectsListPage: string[] = [];

    deletedMembers: PodMember[] = [];
    deletedMembersPage: PodMember[] = [];
    deletedMembersErrorsPageSize = 5;
    deletedMembersErrorsPage = 1;
    deletedMembersErrorsTotalPages: number;

    plantsErrors: StatisticsError[] = [];
    podsErrorsMap: { [podId: string]: StatisticsErrorType } = {};
    alreadyRefreshedOnce = false;
    resourceMismatch = false;


    // exported in HTML
    MEMBER_STATES = MEMBER_STATES;
    PARTICIPATION_TYPES = PARTICIPATION_TYPES;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    SortCriterion = SortCriterion;
    SortDirection = SortDirection;


    constructor(private readonly apiService: SmagApiService,
                private readonly googleApiService: GoogleApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly dialogService: DialogService,
                private readonly fb: FormBuilder) {
        this.reloadMembers = new Subject<void>();
        this.displayedColumns = ['chevron', 'firstName', 'lastName', 'companyName', 'participationType', 'accessType', 'resources', 'status', 'in', 'registered', 'actions'];
        this.community = new BehaviorSubject<Community>({entity: null});
        this.members = new BehaviorSubject<Members>(null);
        this.expandedMember = new BehaviorSubject<Member>(null);
        this.searchForm = this.fb.group({
            filters: this.fb.group({
                status: this.fb.control(MEMBER_STATES),
                hideDisabled: this.fb.control(false),
                podWithoutDate: this.fb.control(false),
                deactivatedPod: this.fb.control(false),
                types: this.fb.control(PARTICIPATION_TYPES[0])
            }),
            textFilter: this.fb.control('')
        });
        this.sortCriteria = [...DEFAULT_SORT_CRITERIA];
        this.msp = new ECMemberStatePipe();
        this.genericPipe = new GenericPipe(i18n);
    }

    ngOnInit() {
        this.hierarchyService.selection.pipe(
            switchMap(entity => this.mkCommunity(entity)),
            takeUntil(this.destroyed)
        ).subscribe(community => {
            this.community.next(community);
        });

        this.hierarchyService.selection.pipe(
            filter(entity => entity !== null),
            switchMap(entity => forkJoin([this.apiService.communityPodsStatistics(entity.id),
                this.apiService.communityPlantsStatistics(entity.id)])),
            takeUntil(this.destroyed)
        ).subscribe(([ecPodsStatistics, ecPlantsStatistics]) => {
            this.plantsErrors = ecPlantsStatistics.errors.filter(error => error.type === StatisticsErrorType.MISSING_PLANT_EFFECTIVE_DATE);
            this.podsErrorsMap = ecPodsStatistics.errors.filter(error => error.type === StatisticsErrorType.MISSING_POD_ACTIVATION_DATE)
                .reduce((errorsMap, error) => ({
                    ...errorsMap,
                    [error.metadata[STATISTICS_ERROR_POD_KEY]]: error.type
                }), {});
        });

        merge(this.searchForm.controls.filters.valueChanges,
            this.searchForm.controls.textFilter.valueChanges.pipe(
                debounceTime(SEARCH_DEBOUNCE_MILLIS),
            )).pipe(
            takeUntil(this.destroyed)
        ).subscribe(() => {
            this.reloadMembers.next();
        });
    }

    ngAfterViewInit() {
        this.paginator.changes.pipe(
            filter((query: QueryList<MatPaginator>) => !!query?.first),
            map((query: QueryList<MatPaginator>) => query.first),
            switchMap(paginator => merge(paginator.page,
                this.reloadMembers.pipe(tap(() => paginator.firstPage()))).pipe(
                startWith(undefined as unknown),
                switchMap(() => this.community),
                switchMap(community => this.mkMembers(community, paginator.pageIndex + 1, paginator.pageSize).pipe(
                    startWith(null as Members), // triggers spinner
                    catchError(err => {
                        this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving subjects'), err);
                        return of(null as Members);
                    }),
                ))
            )),
            takeUntil(this.destroyed)
        ).subscribe(members => {
            this.members.next(members);
            this.expandedMember.next(null);
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get searchFilters(): { [ctrl: string]: FormControl } {
        return (this.searchForm.controls.filters as FormGroup).controls as { [ctrl: string]: FormControl };
    }

    get searchTextFilter(): FormControl {
        return this.searchForm.controls.textFilter as FormControl;
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    mkSelectedStatesTrigger(states: ECMemberState[]): string {
        return states.map(ecMemberState => this.i18n.translate(this.msp.transform(ecMemberState))).join(', ');
    }

    canFilterState(ecMemberState: ECMemberState): boolean {
        const selectedStates = this.searchFilters.status.value as ECMemberState[];
        // is state is currently unselected, then we can select it;
        // otherwise, we can unselect it if and only if, by doing so,
        // the selected state list does not become empty
        return !selectedStates.includes(ecMemberState) || selectedStates.length > 1;
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    onClickSortBy(criterion: SortCriterion, direction: SortDirection) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, direction],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        // reload members
        this.reloadMembers.next();
    }

    onClickNewMember() {
        const formCommunity: FormCommunity = {
            community: this.community.value.community,
            communityData: this.community.value.entity
        };
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Subject creation'),
            alignTitle: ALIGN_TITLE.LEFT,
            template: this.newMemberDialog,
            context: {
                community: formCommunity
            }
        }).closed.then((reload) => {
            if (reload) {
                this.reloadMembers.next();
                this.hierarchyService.reloadHierarchy().then();
            }
        });
    }

    onClickEditMember(member: Member, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        const formCommunity: FormCommunity = {
            community: this.community.value.community,
            communityData: this.community.value.entity
        };
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Edit subject'),
            alignTitle: ALIGN_TITLE.LEFT,
            template: this.newMemberDialog,
            context: {
                member,
                community: formCommunity
            }
        }).closed.then((reload) => {
            if (reload) {
                this.reloadMembers.next();
                this.hierarchyService.reloadHierarchy().then();
            }
        });
    }

    onSelectMember(member: Member) {
        this.expandedMember.next(this.expandedMember.value === member ? null : member);
    }

    onDisableAccount(member: Member) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to disable this user?'),
            template: this.disableMemberDialog,
            context: {
                member
            }
        }).closed.then(push => {
            if (push) {
                this.disableMember(member.member);
            }
        });
    }

    onDeleteUser(member: Member) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to delete this user?'),
            template: this.deleteMemberUserDialog,
            context: {
                member
            }
        }).closed.then(push => {
            if (push) {
                this.deleteUser(member.member);
            }
        });
    }

    onClickDeleteMember(member: Member, event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Delete subject'),
            template: this.deleteMemberDialog,
            context: {
                member
            }
        }).closed.then(del => {
            if (del) {
                this.deleteMember(member.member)
                    .then(() => this.hierarchyService.reloadHierarchy());
            }
        });
    }

    onReactivateAccount(member: Member) {
        this.apiService.enableMemberUser(member.member.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('User re-enabled'));
                return this.mkMember(member.member).toPromise();
            })
            .then((updatedMember) => this.updateModifiedMember(updatedMember, UpdateReason.USER_ACCOUNT_STATE, member.member)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while activating the account'), err);
            });
    }

    onForcePwdChange(member: Member) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to force a password change?'),
            template: this.forcePwdChangeDialog,
            context: {
                firstName: member.member.personal_data?.first_name || '',
                lastName: member.member.personal_data?.last_name || ''
            }
        }).closed.then(change => {
            if (change) {
                this.forcePasswordChange(member.member);
            }
        });
    }

    onReset2FA(member: Member) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Are you sure you want to request re-enabling 2FA?'),
            template: this.force2FAResetDialog,
            context: {
                firstName: member.member.personal_data?.first_name || '',
                lastName: member.member.personal_data?.last_name || ''
            }
        }).closed.then(reset => {
            if (reset) {
                this.force2FAReset(member.member);
            }
        });
    }

    exportMembersToExcel(commId: string, totalMembers: number, communityName: string) {
        this.templateIsDownloading = true;
        this.apiService.exportMembersToExcel(commId, totalMembers, communityName)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateIsDownloading = false;
                this.msgBoxService.info(this.i18n.translate('File downloaded!'));
            }, error => {
                this.templateIsDownloading = false;
                this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'));
            });
    }

    hasPodWithMissingDates(member: Member) {
        const memberPods = member.pods.map(pod => pod.pod?.pod);
        return memberPods.some(memberPod => Object.keys(this.podsErrorsMap).includes(memberPod));
    }

    async uploadMemberFile(data: UploadData): Promise<void> {
        const spinner = this.openSpinner('We are importing the subjects');
        try {
            const response = await this.apiService.uploadMembersTemplateExcel(this.community.value.entity.id, data.fileType,
                {content: data.file.file, filename: data.file.name});

            if (response.success && !response.errors.length) {
                const pods = Object.keys(response.pods_address);
                const coordinatesUpdate = await Promise.all(pods.map(async pod => {
                    try {
                        const coordinates = await this.googleApiService.getCoordinates(addressToString(response.pods_address[pod]));
                        return {pod, coordinates} as EnergyCommunityPodSpatialCoordinateRequest;
                    } catch {
                        return null;
                    }
                }));
                let loadingMessage = this.i18n.translate('Subjects created');
                try {
                    await this.apiService.updatePodsCoordinates(this.community.value.entity.id, coordinatesUpdate.filter(isDefined));
                } catch {
                    loadingMessage = this.i18n.translate('Subjects created: we couldn\'t localize some subjects');
                }
                if (coordinatesUpdate.some(c => !isDefined(c))) {
                    loadingMessage = this.i18n.translate('Subjects created: we couldn\'t localize some subjects');
                }
                spinner.context.next({loading: false, loadingMessage});
                this.reloadMembers.next();
                this.hierarchyService.reloadHierarchy().then();
            } else if (response.errors.length || response.failed_subjects.length) {
                this.massiveImportErrorsList = response.errors
                    .map(resp => this.adaptMassiveImportErrors(resp))
                    .filter(isDefined);
                this.massiveImportErrorsTotalPages = Math.ceil(this.massiveImportErrorsList.length / this.massiveImportErrorsPageSize);
                this.updateMassiveImportErrorsPage();

                this.failedSubjectsList = this.mkSubjectsMessages(response.failed_subjects);
                this.failedSubjectsTotalPages = Math.ceil(this.failedSubjectsList.length / this.failedSubjectsPageSize);
                this.updateFailedSubjectsPage();

                this.successfulSubjectsList = this.mkSubjectsMessages(response.successful_subjects);
                this.successfulSubjectsTotalPages = Math.ceil(this.successfulSubjectsList.length / this.successfulSubjectsPageSize);
                this.updateSuccessfulSubjectsPage();

                spinner.context.next({
                    loading: false,
                    loadingMessage: this.i18n.translate(response.successful_subjects.length ?
                        'Subjects partially created: an error occurred while creating some subjects.' :
                        'An error occurred while creating subjects'),
                    abort: true
                });
                this.dialogService.open<any, number>({
                    template: this.uploadMembersError,
                    context: {
                        message: [
                            this.i18n.translate(response.successful_subjects.length ?
                                'Subjects partially created: an error occurred while creating some subjects.' :
                                'We were unable to create subjects.'),
                            this.i18n.translate('Some values in the file do not appear correct.')]
                    },
                    title: null
                }, {width: DEFAULT_DIALOG_MAX_WIDTH}).closed.then(() => {
                    this.massiveImportErrorsCurrentPage = 1;
                    this.failedSubjectsCurrentPage = 1;
                    this.successfulSubjectsCurrentPage = 1;
                    if (response.successful_subjects.length) {
                        this.reloadMembers.next();
                        this.hierarchyService.reloadHierarchy().then();
                    }
                });
            }
        } catch (error) { // unexpected errors
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate(mkErrorMessage(error, 'An error occurred while creating subjects')),
                abort: true
            });
            this.dialogService.open<any, number>({
                template: this.uploadMembersError,
                context: {
                    message: [
                        this.i18n.translate('We were unable to create subjects.'),
                        this.i18n.translate('Some values in the file do not appear correct.')],
                    errors: []
                },
                title: null,
            }, {width: DEFAULT_DIALOG_MAX_WIDTH}).closed.then(() => {
                this.massiveImportErrorsCurrentPage = 1;
                this.failedSubjectsCurrentPage = 1;
                this.successfulSubjectsCurrentPage = 1;
            });
        }
    }

    updateMassiveImportErrorsPage() {
        const startIndex = (this.massiveImportErrorsCurrentPage - 1) * this.massiveImportErrorsPageSize;
        const endIndex = startIndex + this.massiveImportErrorsPageSize;
        this.massiveImportErrorsListPage = this.massiveImportErrorsList.slice(startIndex, endIndex);
    }

    nextErrorsPage() {
        if (this.massiveImportErrorsCurrentPage < this.massiveImportErrorsTotalPages) {
            this.massiveImportErrorsCurrentPage++;
            this.updateMassiveImportErrorsPage();
        }
    }

    previousErrorsPage() {
        if (this.massiveImportErrorsCurrentPage > 1) {
            this.massiveImportErrorsCurrentPage--;
            this.updateMassiveImportErrorsPage();
        }
    }

    updateFailedSubjectsPage() {
        const startIndex = (this.failedSubjectsCurrentPage - 1) * this.failedSubjectsPageSize;
        const endIndex = startIndex + this.failedSubjectsPageSize;
        this.failedSubjectsListPage = this.failedSubjectsList.slice(startIndex, endIndex);
    }

    nextFailedSubjectsPage() {
        if (this.failedSubjectsCurrentPage < this.failedSubjectsTotalPages) {
            this.failedSubjectsCurrentPage++;
            this.updateFailedSubjectsPage();
        }
    }

    previousFailedSubjectsPage() {
        if (this.failedSubjectsCurrentPage > 1) {
            this.failedSubjectsCurrentPage--;
            this.updateFailedSubjectsPage();
        }
    }

    updateSuccessfulSubjectsPage() {
        const startIndex = (this.successfulSubjectsCurrentPage - 1) * this.successfulSubjectsPageSize;
        const endIndex = startIndex + this.successfulSubjectsPageSize;
        this.successfulSubjectsListPage = this.successfulSubjectsList.slice(startIndex, endIndex);
    }

    nextSuccessfulSubjectsPage() {
        if (this.successfulSubjectsCurrentPage < this.successfulSubjectsTotalPages) {
            this.successfulSubjectsCurrentPage++;
            this.updateSuccessfulSubjectsPage();
        }
    }

    previousSuccessfulSubjectsPage() {
        if (this.successfulSubjectsCurrentPage > 1) {
            this.successfulSubjectsCurrentPage--;
            this.updateSuccessfulSubjectsPage();
        }
    }

    updateDeletedMembersErrorsPage() {
        const startIndex = (this.deletedMembersErrorsPage - 1) * this.deletedMembersErrorsPageSize;
        const endIndex = startIndex + this.deletedMembersErrorsPageSize;
        this.deletedMembersPage = this.deletedMembers.slice(startIndex, endIndex);
    }

    nextMembersErrorsPage() {
        if (this.deletedMembersErrorsPage < this.deletedMembersErrorsTotalPages) {
            this.deletedMembersErrorsPage++;
            this.updateDeletedMembersErrorsPage();
        }
    }

    previousMembersErrorsPage() {
        if (this.deletedMembersErrorsPage > 1) {
            this.deletedMembersErrorsPage--;
            this.updateDeletedMembersErrorsPage();
        }
    }

    adaptMassiveImportErrors(error: SubjectUploadError): string {
        switch (error.code) {
            case ErrorCode.MISSING_REQUIRED_SHEET:
                return this.i18n.translate('Missing required Sheet for subjects massive import: {0}', error.metadata.sheet);
            case ErrorCode.MAXIMUM_ROW_NUMBER_EXCEEDED:
                return this.i18n.translate('Maximum row number exceeded for sheet \'{0}\': expected {1} got {2}',
                    error.metadata.sheet,
                    error.metadata.max_rows,
                    error.metadata.rows);
            case ErrorCode.INVALID_FILE_TYPE:
                const templateType = this.i18n.translate(processTemplateType(error.metadata.template as ECMembersUploadTemplateType));
                return this.i18n.translate('Invalid file type \'{0}\': header col \'{1}\' doesn\'t match expected value in sheet \'{2}\'. Make sure you selected the correct template file.',
                    templateType,
                    error.metadata.column,
                    error.metadata.sheet);
            case ErrorCode.MISSING_REQUIRED_VALUES:
                return this.i18n.translate('Missing required value \'{0}\' in sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_POD_ACTIVATION_DATE:
                return this.i18n.translate('Invalid activation date for POD {0} in sheet \'{1}\'',
                    error.metadata.pod,
                    error.metadata.sheet);
            case ErrorCode.POD_ALREADY_EXISTING:
                return this.i18n.translate('POD {0} already exists', error.metadata.pod);
            case ErrorCode.INVALID_MEMBER_JURIDICAL_TYPE:
                return this.i18n.translate('Invalid member juridical type {0} in sheet \'{1}\' at row {2}',
                    error.metadata.subject_juridical_form,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_SUBJECT_LEGAL_FORM:
                return this.i18n.translate('Invalid legal form {0} in sheet \'{1}\' at row {2}',
                    error.metadata.legal_form,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_SUBJECT_SUBJECT_TYPE:
                return this.i18n.translate('Invalid subject type {0} in sheet \'{1}\' at row {2}',
                    error.metadata.subject_corporate_type,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_SUBJECT_ENTITY_SUBTYPE:
                return this.i18n.translate('Invalid entity subtype {0} in sheet \'{1}\' at row {2}, check matching pod utility types',
                    error.metadata.subject_corporate_subtype,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_POD_UTILITY_TYPE_FOR_SUBJECT:
                return this.i18n.translate('Invalid pod utility type for POD {0} with legal form {1} in sheet \'{2}\' at row {3}',
                    error.metadata.pod,
                    error.metadata.legal_form,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_PV_PLANT_FOR_POD:
                return this.i18n.translate('{0}: invalid pv plant', mkBaseError(error, this.i18n));
            case ErrorCode.INVALID_POD_FOR_SUBJECT:
                return this.i18n.translate('{0}: invalid pod for subject', mkBaseError(error, this.i18n));
            case ErrorCode.INVALID_PARTICIPATION_TYPE:
                return this.i18n.translate('Invalid energy community participation type {0}', error.metadata.participation_type);
            case ErrorCode.INVALID_MEMBER_FIRST_NAME:
                return this.i18n.translate('Invalid subject first name \'{0}\' in sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_LAST_NAME:
                return this.i18n.translate('Invalid subject last name \'{0}\' in sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_ACCESS_TYPE:
                return this.i18n.translate('Value\'{0}\' for parameter \'Access Type\' is invalid for sheet \'{1}\' at row {2}',
                    this.i18n.translate(error.metadata.access_type),
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_EMAIL:
                return this.i18n.translate('Email {0} is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.MEMBER_EMAIL_NOT_UNIQUE:
                return this.i18n.translate('Email {0} is shared by {1} subjects',
                    error.metadata.email,
                    error.metadata.members);
            case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING:
                return this.i18n.translate('Email address {0} already exists for some other member',
                    error.metadata.email);
            case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING_FOR_USER:
                return this.i18n.translate('Email {0} already exists',
                    error.metadata.email);
            case ErrorCode.INVALID_EMAIL:
                return this.i18n.translate('Email {0} is not valid',
                    error.metadata.email);
            case ErrorCode.INVALID_MOBILE:
                return this.i18n.translate('Mobile {0} is not valid',
                    error.metadata.mobile);
            case ErrorCode.INVALID_MEMBER_ID:
                return this.i18n.translate('Subject id is invalid for sheet \'{0}\' at row {1}: do not use formula in the sheet.',
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.MEMBER_MOBILE_NOT_UNIQUE:
                return this.i18n.translate('Mobile number {0} is shared by {1} subjects',
                    error.metadata.mobile,
                    error.metadata.members);
            case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING:
                return this.i18n.translate('Mobile {0} already exists for some other subjects',
                    error.metadata.mobile);
            case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING_FOR_USER:
                return this.i18n.translate('Mobile {0} already exists for some other users',
                    error.metadata.mobile);
            case ErrorCode.POD_RESOURCE_ALREADY_EXISTING:
                return this.i18n.translate('A resource with POD \'{0}\' already exists', error.metadata.pod);
            case ErrorCode.POD_ACTIVE_BEFORE_COMMUNITY:
                return this.i18n.translate('POD \'{0}\' is active before its community', error.metadata.pod);
            case ErrorCode.INVALID_MEMBER_TAX_ID:
                return this.i18n.translate('Tax Id \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_VAT_ID:
                return this.i18n.translate('VAT Id \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_POSTAL_CODE:
                return this.i18n.translate('Postal code \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_IBAN:
                return this.i18n.translate('IBAN \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_COMPANY_NAME:
                return this.i18n.translate('Company name \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_MEMBER_ENERGY_PROVIDER:
                return this.i18n.translate('Energy provider \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_POD_UTILITY_TYPE:
                return this.i18n.translate('POD utility type \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_POD:
                return this.i18n.translate('POD \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_NOMINAL_POWER:
                return this.i18n.translate('Nominal Power \'{0}\'is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.MISSING_PVS_FOR_PRODUCER:
                return this.i18n.translate('Flag \'{0}\' is invalid if no PV is associated to the pod: sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_CONTRACTUAL_POWER:
                return this.i18n.translate('Contractual power \'{0}\' is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_POD_TYPE_FOR_THIRD_PARTY_PRODUCER:
                return this.i18n.translate('POD \'{0}\' has wrong value in column \'{1}\' given its \'{2}\' participation type',
                    error.metadata.pod,
                    error.metadata.column,
                    this.genericPipe.transform(error.metadata.participation_type));
            case ErrorCode.DUPLICATE_PRODUCTION_POD_VALUES:
                return this.i18n.translate('Value of production POD \'{0}\' identical for multiple plants in column \'{1}\'',
                    error.metadata.pod,
                    error.metadata.column);
            case ErrorCode.INVALID_CAPITAL_GRANT_FOR_PV_PLANT:
                return this.i18n.translate('Capital grant \'{0}%\' is invalid for sheet \'{1}\' at row {2}',
                    error.metadata.column,
                    error.metadata.sheet,
                    error.metadata.row);
            case ErrorCode.INVALID_THIRD_PARTY_ACCESS_TYPE:
                return this.i18n.translate('Subject \'{0}\' has wrong access type value in column \'{1}\' given its \'{2}\' participation type',
                    error.metadata.members,
                    error.metadata.column,
                    this.genericPipe.transform(error.metadata.participation_type));
            default:
                return null;
        }
    }

    mkPodResources(member: Member): PodResource[] {
        return member.pods.filter(pod => !!pod.resource)
            .map(pod => ({
                pod: pod.pod.pod,
                entity: pod.resource.entity,
            } as PodResource));
    }

    onclickUploadExcel() {
        this.dialogService.open<any, number>({
            template: this.uploadMembersDialog,
            context: {
                communityId: this.community.value.entity.id,
            },
            alignTitle: ALIGN_TITLE.LEFT,
            title: this.i18n.translate('Create multiple subjects')
        }).closed.then();
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }

    private deleteMember(member: ECMember): Promise<void> {
        return this.apiService.deleteMember(member.id, true)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Subject removed'));
                this.reloadMembers.next();
            }).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while removing subject'), err));
    }

    private disableMember(member: ECMember) {
        this.apiService.disableMemberUser(member.id)
            .then(() => this.apiService.loadMember(member.id).toPromise())
            .then(loadedMember => {
                this.msgBoxService.info(this.i18n.translate('User disabled'));
                return this.mkMember(loadedMember).toPromise();
            })
            .then((updatedMember) => this.updateModifiedMember(updatedMember, UpdateReason.USER_ACCOUNT_STATE, member)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while disabling user'), err);
            });
    }

    private deleteUser(member: ECMember) {
        this.apiService.detachMemberUser(member.id)
            .then(() => this.apiService.loadMember(member.id).toPromise())
            .then(loadedMember => {
                this.msgBoxService.info(this.i18n.translate('User Deleted'));
                return this.mkMember(loadedMember).toPromise();
            })
            .then((updatedMember) => this.updateModifiedMember(updatedMember, UpdateReason.USER_ACCOUNT_STATE, member)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while deleting user'), err);
            });
    }

    private forcePasswordChange(member: ECMember) {
        this.apiService.forceMemberUserChecks(member.id, MISSING_CHECKS.CHANGE_PASSWORD)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Password renewal forced'));
                return this.mkMember(member).toPromise();
            })
            .then((updatedMember) => this.updateModifiedMember(updatedMember, UpdateReason.USER_AUTH, member)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while forcing password renewal'), err);
            });
    }

    private force2FAReset(member: ECMember) {
        this.apiService.forceMemberUserChecks(member.id, MISSING_CHECKS.ENABLE_TOTP)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('2FA reset'));
                return this.mkMember(member).toPromise();
            })
            .then((updatedMember) => this.updateModifiedMember(updatedMember, UpdateReason.USER_AUTH, member)) // substitute only updated member
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('An error occurred while resetting 2FA'), err);
            });
    }

    private updateModifiedMember(modifiedMember: Member, reason: UpdateReason, oldMember: ECMember) {
        // check if an overall member reloading is due
        // NOTE: if member reloading is performed, expanded member is cleared as well
        switch (reason) {
            case UpdateReason.USER_ACCOUNT_STATE:
                if (this.searchFilters.hideDisabled.value) {
                    this.reloadMembers.next();
                    return;
                }
                break;
            case UpdateReason.USER_AUTH:
                break;
            case UpdateReason.STATE:
                if (this.searchFilters.status.value.length < MEMBER_STATES.length) {
                    // do not reload if *all* member states are selected
                    this.reloadMembers.next();
                    return;
                }
                break;
            case UpdateReason.FIELDS:
                if (this.needsReloading(oldMember, modifiedMember.member)) {
                    this.reloadMembers.next();
                    return;
                }
                break;
        }
        // update modified members (no reload needed)
        // const updatedMembers = _.fromPairs(members.map(member => [member.member.id, member]));
        this.members.next({
            members: this.members.value.members.map(m => m.member.id === modifiedMember.member.id ? modifiedMember : m),
            total: this.members.value.total
        });
        if (this.expandedMember.value && this.expandedMember.value.member.id === modifiedMember.member.id) {
            // update selected member, if it has been modified
            this.expandedMember.next(modifiedMember);
        }
    }

    private mkCommunity(entity: HierarchyEntity): Observable<Community> {
        if (entity?.type !== EntityType.ENERGY_COMMUNITY || !can(entity.permissions, Permission.READ)) {
            return of({entity: null});
        }
        return this.apiService.getEntity(entity.id).pipe(
            map((community: EnergyCommunity) => ({
                entity,
                canView: can(entity.permissions, Permission.READ),
                canUpdate: can(entity.permissions, Permission.UPDATE),
                community
            }))
        );
    }

    private mkTypes(selectedType: string) {
        return ([selectedType] || []).filter(value => Object.values(ECMemberParticipationType).includes(value as ECMemberParticipationType)) as ECMemberParticipationType[];
    }

    private mkMembers(community: Community, page: number, size: number): Observable<Members> {
        if (!community.community || !community.canView) {
            return of({members: [], total: 0});
        }
        // filtering conditions
        const textFilter: string = this.searchTextFilter.value?.trim() || null;
        const states: ECMemberState[] = this.searchFilters.status.value || [];
        const types: ECMemberParticipationType[] = this.mkTypes(this.searchFilters.types.value);
        const accountDisabled: boolean = this.searchFilters.hideDisabled.value ? false : null;
        const podWithoutDate: boolean = this.searchFilters.podWithoutDate.value;
        const deactivatedPod: boolean = this.searchFilters.deactivatedPod.value;
        // sorting criteria
        const orderBy: string[] = this.sortCriteria.map(([criterion, direction]) => `${criterion}_${direction}`);
        return this.apiService.listMembers(community.entity.id, page, size,
            true,
            true,
            true, // OR-search over "text" fields
            null,
            textFilter,
            textFilter, // first name, last name, company name
            textFilter, // email
            textFilter, // POD
            states,
            types,
            accountDisabled,
            null,
            podWithoutDate,
            deactivatedPod,
            orderBy)
            .pipe(switchMap(resp => resp.results.length ?
                forkJoin(resp.results.map(member => this.mkMember(member))).pipe(
                    flatMap(async members => {

                        await this.findInvalidMembers(members);


                        return {
                            members,
                            total: resp.total
                        } as Members;
                    })
                ) :
                of({members: [], total: 0} as Members))
            );
    }

    private async findInvalidMembers(members: Member[]) {
        const toDelete = members.filter(member => isDefined(member.resources) && member.resources.length === 0);

        const okMembers = members.filter(member => isDefined(member.resources) && member.resources.length > 0);

        if (toDelete.length > 0) {

            if (!this.resourceMismatch) {
                this.resourceMismatch = true;
                return {toDelete, okMembers};
            }

            this.deletedMembers = _.flatMap(toDelete, member => {
                return _.map(member.pods, pod => {
                    return {
                        pod: pod.pod.pod,
                        name: isDefined(member.member.personal_data.company_name) ? member.member.personal_data.company_name : member.member.personal_data.first_name.concat(' ', member.member.personal_data.last_name),
                    } as PodMember;
                });
            });
            this.deletedMembersErrorsTotalPages = Math.ceil(this.deletedMembers.length / this.deletedMembersErrorsPageSize);
            this.updateDeletedMembersErrorsPage();
            this.dialogService.open<any, boolean>({
                title: this.i18n.translate('There were errors while creating some subjects'),
                template: this.memberNotCreatedDialog
            }).closed.then();
        }
        return {toDelete, okMembers};
    }

    private mkMember(member: ECMember): Observable<Member> {
        return forkJoin([
            this.hierarchyService.hierarchyIds.pipe(first()),
            member.pods.length ?
                forkJoin(member.pods.map(pod => this.apiService.findEntityByPOD(pod.pod).pipe(map(resp => resp?.id)))) :
                of([] as string[]),
            member.u3id ? this.apiService.getMemberUser(member.id) : of(null)
        ]).pipe(
            map(([entities, podEntities, user]) => [
                member.pods.map((pod, i) => {
                    if (!(podEntities[i] && entities[podEntities[i]])) {
                        // Refreshing once is needed due to a mismatch between hierarchyIds and members pods after
                        // a PMS deployment. If after a refresh the mismatch is still there, then we proceed with
                        // deleting the members.
                        if (!this.alreadyRefreshedOnce) {
                            this.alreadyRefreshedOnce = true;
                            this.hierarchyService.reloadHierarchy().then();
                        }
                    }
                    return ({
                        pod,
                        resource: podEntities[i] && entities[podEntities[i]] ? {
                            entity: entities[podEntities[i]],
                            canView: can(entities[podEntities[i]].permissions, Permission.READ)
                        } as Resource : null
                    } as Pod);
                }),
                user
            ] as [Pod[], User]),
            map(([pods, user]) => ({
                name: mkMemberName(member),
                member,
                pods,
                account: user ? {
                    enabled: !user.disabled,
                    mustChangePassword: (user.missing_checks || []).includes(MISSING_CHECKS.CHANGE_PASSWORD),
                    pwExpiresOn: user.password_expires_on,
                    mandatory2fa: (user.missing_checks || []).includes(MISSING_CHECKS.ENABLE_TOTP) || !!user['2fa_enabled'],
                    enabled2fa: !!user['2fa_enabled'],
                    disableReason: user.disable_reason,
                    lastAccessOn: user.last_access_on,
                    disabledOn: user.disabled_on
                } : null,
                resources: pods.map(pod => pod.resource).filter(resource => !!resource)
            } as Member))
        );
    }

    private needsReloading(oldMember: ECMember, updatedMember: ECMember): boolean {
        if (!!this.searchTextFilter.value?.trim()) {
            if (((oldMember.personal_data.email || null) !== (updatedMember.personal_data.email || null)) ||
                ((oldMember.personal_data.last_name || null) !== (updatedMember.personal_data.last_name || null)) ||
                ((oldMember.personal_data.company_name || null) !== (updatedMember.personal_data.company_name || null)) ||
                newOrRemovedPods(oldMember.pods, updatedMember.pods)) {
                return true;
            }
        }
        return false;
    }

    private mkSubjectsMessages(failedSubjects: UploadedSubjectInfo[]): string[] {
        return failedSubjects.map(subjectInfo => isDefined(subjectInfo.first_name) && isDefined(subjectInfo.last_name) ?
            `${subjectInfo.first_name} ${subjectInfo.last_name}` :
            subjectInfo.company_name);
    }
}

function newOrRemovedPods(oldPods: ECMemberPOD[], newPods: ECMemberPOD[]): boolean {
    return !!newPods.find(pod => !oldPods.find(p => p.pod === pod.pod)) ||
        !!oldPods.find(pod => !newPods.find(p => p.pod === pod.pod));
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING:
            return 'A subject with this email already exists';
        case ErrorCode.MEMBER_EMAIL_ALREADY_EXISTING_FOR_USER:
            return 'A user with this email already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING:
            return 'A subject with this mobile number already exists';
        case ErrorCode.MEMBER_MOBILE_ALREADY_EXISTING_FOR_USER:
            return 'A user with this mobile number already exists';
        case ErrorCode.POD_CONNECTED_TO_ENTITY_NOT_IN_COMMUNITY:
            return 'POD connects member to a resource that is not part of this Energy Community';
        case ErrorCode.MISSING_REQUIRED_VALUES:
            return 'A required value is missing';
        case ErrorCode.INVALID_POD_ACTIVATION_DATE:
            return 'Invalid POD activation date';
        case ErrorCode.INVALID_FILE_TYPE:
            return 'Invalid file type';
        case ErrorCode.INVALID_MEMBER_JURIDICAL_TYPE:
            return 'Invalid energy community member juridical type provided';
        case ErrorCode.INVALID_POD_UTILITY_TYPE:
            return 'Invalid POD utility type provided';
        case ErrorCode.INVALID_DATE:
            return 'Invalid date provided';
        default:
            return fallback;
    }
}

function processTemplateType(tp: ECMembersUploadTemplateType): string {
    switch (tp) {
        case ECMembersUploadTemplateType.STATISTICAL:
            return 'Statistical';
        case ECMembersUploadTemplateType.MONTHLY_SINGLE_HOURLY:
            return 'Single Hour';
        case ECMembersUploadTemplateType.MONTHLY_TIME_SLOTS:
            return 'Time Slots';
    }
}


